import { Form, Link } from '@appsumo/dorado-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as yup from 'yup';
import { Modal } from '@appsumo/dorado-react';
import reactStringReplace from 'react-string-replace';
import useUser from '~/lib/user';
import type { Deal } from '~/types/deal';
import { fetchJson } from '~/lib/fetch';
import { useUserStatus } from '~/hooks/userStatus';
import { useRouter } from 'next/router';
import { getQueryFromPath } from '~/lib/util';

type TextType = 'review' | 'comment' | 'reply';
const TextTypes: Record<
  TextType,
  {
    title: string;
    prompt: string;
    placeholder: string;
    notifyLabel: string;
    instructionTitle: string;
    instructions: string[];
  }
> = {
  review: {
    title: 'Submit a review',
    prompt: 'Review',
    placeholder: 'Enter your review',
    notifyLabel: 'Notify me via email when someone replies to this review',
    instructionTitle:
      'Please check these guidelines before submitting your review:',
    instructions: [
      "Please review the product you're purchasing, not the terms of the deal.",
      'Add value when leaving a review.',
      'Try the product before reviewing it!',
    ],
  },
  comment: {
    title: 'Submit a question',
    prompt: 'Your question',
    placeholder: 'Enter your question',
    notifyLabel: 'Notify me via email when someone replies to this question',
    instructionTitle:
      'Please check these guidelines before submitting your question:',
    instructions: [
      'Check the FAQ and pinned comments before you post, your question may have already been answered!',
      'Please don’t use this forum as a platform for requesting deal features or new terms.',
      'If you have an AppSumo support related issue, shoot us an email at ||support@appsumo.com||.',
      'Be respectful of your fellow Sumo-lings and partners – rudeness or harassment will not be tolerated.',
    ],
  },
  reply: {
    title: 'Reply to {username}',
    prompt: 'Your reply',
    placeholder: 'Enter your response',
    notifyLabel: 'Notify me via email for follow-ups',
    instructionTitle:
      'Please check these guidelines before submitting a review:',
    instructions: [
      'Please don’t use this forum as a platform for requesting deal features or new terms.',
      'If you have an AppSumo support related issue, shoot us an email at ||support@appsumo.com||.',
      'Be respectful of your fellow Sumo-lings and partners – rudeness or harassment will not be tolerated.',
    ],
  },
};

const QuestionSchema = yup.object().shape({
  comment: yup.string().required(),
  notify: yup.boolean(),
});

const ReviewSchema = yup.object().shape({
  title: yup.string().required(),
  rating: yup.number().min(1).max(5).required(),
  comment: yup.string().required(),
  notify: yup.boolean(),
});

interface QuestionSchema extends yup.InferType<typeof QuestionSchema> {
  title?: never;
  rating?: never;
}
interface ReviewSchema extends yup.InferType<typeof ReviewSchema> {}

type QuestionOrReviewSchema = QuestionSchema | ReviewSchema;

interface ReplyModalProps {
  comment?: any;
  type: 'dealcomment' | 'dealreview';
  deal: Deal;
  hideModal: Function;
  mutateComment: Function;
}

export function ReplyModal({
  comment,
  type,
  deal,
  hideModal,
  mutateComment,
}: ReplyModalProps) {
  const { user } = useUser();
  const formRef = useRef<HTMLFormElement>(null);
  const { mutateUserStatus } = useUserStatus(deal.id);

  const handleSubmit = useCallback(
    (values: QuestionOrReviewSchema) => {
      const post = {
        object_pk:
          type === 'dealcomment'
            ? deal.discussion.object_pk_question
            : deal.discussion.object_pk_review,
        reply_to: comment?.id || 0,
        content_type: `deals.${type}`,
        timestamp: deal.discussion.timestamp,
        security_hash: deal.discussion.hash_question,
        comment: values.comment,
        followup: values.notify,
        honeypot: '',
        email: user?.email,
        name: user?.username,
        title: type === 'dealreview' && values.title ? values.title : '',
        rating: type === 'dealreview' ? values.rating : 0,
      };

      const urlEncodedPost = Object.entries(post)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');

      fetchJson('/comments/api/discussion/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedPost,
      }).then(() => {
        mutateComment();
        mutateUserStatus();
        hideModal();
      });
    },
    [comment, user, deal, mutateComment, hideModal, type, mutateUserStatus],
  );

  const isReply = !!comment;
  const isReview = type === 'dealreview';

  // Set initial rating from query param
  const { asPath } = useRouter();
  const params = getQueryFromPath(asPath);
  const rating = Number(params.get('rating')) || 5;
  const initialRating = isReply ? 0 : rating;

  const textType: TextType = useMemo(() => {
    if (isReply) {
      return 'reply';
    }

    if (isReview) {
      return 'review';
    }

    return 'comment';
  }, [isReview, isReply]);

  const modalTitle = useMemo(
    () =>
      TextTypes[textType].title.replace('{username}', comment?.user?.username),
    [textType, comment?.user?.username],
  );

  const instructions = useMemo(
    () =>
      TextTypes[textType].instructions.map((instruction, index) => (
        <li key={index}>
          {reactStringReplace(instruction, /\|\|([^|]+)/, (match, index) => (
            <a
              href={`mailto:${match}`}
              className="text-bolt hover:underline"
              key={index}
            >
              {match}
            </a>
          ))}
        </li>
      )),
    [textType],
  );

  useEffect(() => {
    const titleElement = document.querySelector(
      'input[name="title"]',
    ) as HTMLInputElement;
    setTimeout(
      () => titleElement?.focus({ focusVisible: true } as FocusOptions),
      0,
    );
  }, []);

  return (
    <Modal
      title={modalTitle}
      hideModal={hideModal}
      className="max-h-[calc(100vh-4rem)] overflow-y-auto"
      actionButton={{
        label: 'Submit',
        callback: (ev: Event) => {
          ev.preventDefault();
          formRef?.current?.submit();
        },
      }}
      cancelButton={{ label: 'Cancel', callback: hideModal }}
    >
      <Form
        ref={formRef}
        resolver={yupResolver(
          isReply || type === 'dealcomment' ? QuestionSchema : ReviewSchema,
        )}
        onSubmit={handleSubmit}
        defaultValues={{ rating: initialRating }}
      >
        <div className="flex flex-col gap-y-4">
          {isReply && (
            <div className="rounded bg-iceberg p-4">
              {comment.title && (
                <p className="mb-4 font-bold">{comment.title}</p>
              )}
              <p>{comment.comment}</p>
            </div>
          )}
          {!isReply && isReview && (
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Input
                name="title"
                label="Title"
                placeholder="Enter your title"
              />
              <Form.StarRating
                name="rating"
                label="Click to provide your taco rating"
              />
            </div>
          )}
          <div>
            <Form.TextArea
              name="comment"
              label={TextTypes[textType].prompt}
              placeholder={TextTypes[textType].placeholder}
            />
          </div>
          <div>
            <Form.Checkbox
              name="notify"
              label={TextTypes[textType].notifyLabel}
            />
          </div>
          <div className="rounded bg-iceberg p-4 text-sm">
            <h5 className="mb-2 font-bold">
              {TextTypes[textType].instructionTitle}
            </h5>
            <ul className="flex flex-col gap-y-2">{instructions}</ul>
          </div>
          <div className="text-sm">
            By submitting, I agree to{' '}
            <Link href="/terms" styled>
              AppSumo&apos;s community guidelines
            </Link>
          </div>
        </div>
      </Form>
    </Modal>
  );
}
